import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot, doc } from 'firebase/firestore';
import { isInternalUser } from '../../services/accessControlService';
import {
  createAccountWebsiteAudit,
  getAuditStatus,
  transformAuditResults,
} from '../../services/websiteAuditService';
import {
  WEBSITE_AUDIT_PIPELINE_STEPS,
  getStepStatus,
  metricsFromDomainMetrics,
  EMPTY_AUDIT_METRICS,
  formatMetricValue,
  formatLoadSpeed,
  hasOnPageMetrics,
  mergeAuditMetrics,
  auditDocToTransformSource,
} from '../../utils/websiteAuditPipeline';
import { SectionHeader, SUBCARD, INPUT, TONES } from './AssessmentAnalysisUi';

const MetricCard = ({ label, value, dimmed = false }) => (
  <div className={`${SUBCARD} p-4 text-center ${dimmed ? 'border-dashed opacity-60' : ''}`}>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</p>
    <p className={`mt-1 text-xl font-bold tracking-tight ${dimmed ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}>
      {value}
    </p>
  </div>
);

const AuditPipelineProgress = ({ pipelineStep, pipelineMessage, pipelineLog, auditStatus, darkMode }) => {
  const logEntries = Array.isArray(pipelineLog) ? pipelineLog : [];

  return (
    <div
      className={`mb-4 rounded-lg p-4 border ${
        darkMode ? 'bg-gray-800/80 border-gray-600' : 'bg-gray-50 border-gray-200'
      }`}
    >
      <p className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
        Audit pipeline
        {pipelineMessage && (
          <span className={`block mt-1 font-normal text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {pipelineMessage}
          </span>
        )}
      </p>
      <ol className="space-y-2">
        {WEBSITE_AUDIT_PIPELINE_STEPS.map((step) => {
          const status = getStepStatus(step.id, pipelineStep, auditStatus);
          const isActive = status === 'active';
          const isDone = status === 'done';
          const isError = status === 'error';

          return (
            <li key={step.id} className="flex items-start gap-2 text-sm">
              <span
                className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                  isError
                    ? 'bg-red-500 text-white'
                    : isDone
                      ? 'bg-green-500 text-white'
                      : isActive
                        ? 'bg-blue-500 text-white animate-pulse'
                        : darkMode
                          ? 'bg-gray-600 text-gray-400'
                          : 'bg-gray-200 text-gray-500'
                }`}
              >
                {isError ? '!' : isDone ? '✓' : isActive ? '…' : ''}
              </span>
              <div className="min-w-0 flex-1">
                <span
                  className={
                    isActive
                      ? darkMode
                        ? 'text-blue-300 font-medium'
                        : 'text-blue-700 font-medium'
                      : isDone
                        ? darkMode
                          ? 'text-gray-300'
                          : 'text-gray-700'
                        : darkMode
                          ? 'text-gray-500'
                          : 'text-gray-400'
                  }
                >
                  {step.label}
                </span>
                <span className={`ml-2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  ({step.fn})
                </span>
              </div>
            </li>
          );
        })}
      </ol>
      {logEntries.length > 0 && (
        <details className={`mt-4 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <summary className="cursor-pointer font-medium">Debug log ({logEntries.length} entries)</summary>
          <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto font-mono">
            {logEntries.map((entry, i) => (
              <li key={`${entry.step}-${entry.at}-${i}`}>
                [{entry.at}] {entry.step}: {entry.message}
                {entry.fn ? ` — ${entry.fn}` : ''}
                {entry.dataForSeoStatus ? ` (${entry.dataForSeoStatus})` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

const AuditMetricsGrid = ({ data, hasCompletedAudit, darkMode }) => {
  const dimmed = !hasCompletedAudit;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <MetricCard
        label="SEO Score"
        value={data.seoScore != null ? `${data.seoScore}/100` : '—'}
        darkMode={darkMode}
        dimmed={dimmed && data.seoScore == null}
      />
      <MetricCard
        label="On-Page Score"
        value={data.onPageScore != null ? `${data.onPageScore}/100` : '—'}
        darkMode={darkMode}
        dimmed={dimmed && data.onPageScore == null}
      />
      <MetricCard
        label="Organic Traffic (ETV)"
        value={formatMetricValue(data.organicTraffic?.amount)}
        darkMode={darkMode}
        dimmed={dimmed && data.organicTraffic?.amount == null}
      />
      <MetricCard
        label="Ranking Keywords"
        value={formatMetricValue(
          data.organicTraffic?.rankingKeywords ?? data.rankedKeywords?.total
        )}
        darkMode={darkMode}
        dimmed={dimmed && data.organicTraffic?.rankingKeywords == null && data.rankedKeywords?.total == null}
      />
      <MetricCard
        label="Top 10 Keywords"
        value={formatMetricValue(data.rankedKeywords?.top10)}
        darkMode={darkMode}
        dimmed={dimmed && data.rankedKeywords?.top10 == null}
      />
      <MetricCard
        label="Backlinks"
        value={formatMetricValue(data.backlinkProfile?.backlinks)}
        darkMode={darkMode}
        dimmed={dimmed && data.backlinkProfile?.backlinks == null}
      />
      <MetricCard
        label="Page Load Speed"
        value={formatLoadSpeed(data.pageAudit?.loadSpeed)}
        darkMode={darkMode}
        dimmed={dimmed && data.pageAudit?.loadSpeed == null}
      />
      <MetricCard
        label="Critical Issues"
        value={formatMetricValue(data.errorsWarnings?.criticalErrors)}
        darkMode={darkMode}
        dimmed={dimmed && data.errorsWarnings?.criticalErrors == null}
      />
    </div>
  );
};

/** Extra technical fields surfaced when the audit provides them. */
const AuditExtraDetails = ({ data, hasOnPage }) => {
  const num = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : null);
  const rows = [
    { label: 'Warnings', value: num(data.errorsWarnings?.warnings), show: hasOnPage },
    { label: 'Notices', value: num(data.errorsWarnings?.notices), show: hasOnPage },
    { label: 'SEO Issues', value: num(data.errorsWarnings?.seoIssues), show: hasOnPage },
    { label: 'Performance Issues', value: num(data.errorsWarnings?.performanceIssues), show: hasOnPage },
    { label: 'Accessibility Issues', value: num(data.errorsWarnings?.accessibilityIssues), show: hasOnPage },
    { label: 'Content Score', value: num(data.contentScore), suffix: '/100', show: hasOnPage && num(data.contentScore) > 0 },
    { label: 'Spam Score', value: num(data.backlinkProfile?.spamScore), suffix: '%', show: num(data.backlinkProfile?.spamScore) > 0 },
    { label: 'Nofollow Links', value: num(data.backlinkProfile?.nofollowLinks), show: num(data.backlinkProfile?.nofollowLinks) > 0 },
    { label: 'Est. Traffic Value', value: num(data.organicTraffic?.estimatedTrafficValue), prefix: '$', show: num(data.organicTraffic?.estimatedTrafficValue) > 0 },
    { label: 'Total Keywords', value: num(data.rankedKeywords?.total), show: num(data.rankedKeywords?.total) > 0 },
    { label: 'Avg Monthly Traffic', value: num(data.historicalTraffic?.averageETV), show: num(data.historicalTraffic?.averageETV) > 0 },
    { label: 'Peak Monthly Traffic', value: num(data.historicalTraffic?.highestETV), show: num(data.historicalTraffic?.highestETV) > 0 },
  ].filter((r) => r.show && r.value !== null);

  if (rows.length === 0) return null;
  return (
    <div className="mt-6">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 text-center mb-3">
        Additional details
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {rows.map((r) => (
          <div key={r.label} className={`${SUBCARD} px-4 py-3 text-center`}>
            <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">{r.label}</p>
            <p className="mt-0.5 text-base font-semibold text-gray-900 dark:text-white">
              {r.prefix || ''}
              {Math.round(r.value).toLocaleString()}
              {r.suffix || ''}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- AI search (DataForSEO AI Optimization) detail block ---------- */

// Tiny 12-month sparkline for AI keyword volume trends (same shape as the portal Website page).
const TrendSparkline = ({ trend }) => {
  const points = (trend || []).map((t) => Number(t.volume) || 0);
  if (points.length < 2) return null;
  const max = Math.max(...points, 1);
  const w = 64;
  const h = 18;
  const step = w / (points.length - 1);
  const d = points.map((v, i) => `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(1)} ${(h - (v / max) * (h - 2) - 1).toFixed(1)}`).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="inline-block align-middle text-red-500">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const AiSearchDetails = ({ ai }) => {
  const hasAi = Boolean(ai) && (((ai.totalAiVolume || 0) > 0) || ((ai.llmMentions?.totalMentions || 0) > 0) || (ai.keywords || []).length > 0);
  const cited = Boolean(ai?.llmMentions?.cited);
  const tile = `${SUBCARD} p-4 text-center`;
  const inner = 'p-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700 text-center';

  return (
    <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
      <div className="flex flex-col items-center text-center mb-4">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
          <span className="text-xl leading-none" aria-hidden="true">🤖</span>
          <span>AI Search Visibility</span>
        </h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 max-w-xl">
          DataForSEO AI Optimization: how often these keywords are searched inside AI tools, and whether AI answers cite this business.
        </p>
      </div>

      {!hasAi ? (
        <p className="text-sm text-center text-gray-500 dark:text-gray-400 py-4">
          AI search insights appear after an audit runs with keywords available. Nothing was returned for this site yet.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className={tile}>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{(ai.totalAiVolume || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Monthly AI searches for tracked keywords</p>
            </div>
            <div className={tile}>
              <p className={`text-2xl font-bold ${cited ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400'}`}>
                {cited ? 'Yes' : 'Not yet'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Do AI tools recommend this business?</p>
            </div>
            <div className={tile}>
              <p className="text-lg font-bold text-gray-900 dark:text-white truncate">{ai.topKeyword?.keyword || '—'}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Top AI-searched keyword</p>
            </div>
          </div>

          {(ai.keywords || []).length > 0 && (
            <div className={`${SUBCARD} mt-4 p-4`}>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white text-center mb-3">AI Keyword Search Volume</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400">
                      <th className="text-left py-2 font-medium">Keyword</th>
                      <th className="text-right py-2 font-medium">AI Searches / mo</th>
                      <th className="text-center py-2 font-medium">12-Month Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ai.keywords.slice(0, 15).map((k, i) => (
                      <tr key={i} className="border-b border-gray-100 dark:border-gray-700/60 last:border-b-0">
                        <td className="py-2 text-gray-700 dark:text-gray-200">{k.keyword}</td>
                        <td className="py-2 text-right font-medium text-gray-900 dark:text-white">{(k.aiSearchVolume || 0).toLocaleString()}</td>
                        <td className="py-2 text-center whitespace-nowrap">
                          <TrendSparkline trend={k.trend} />
                          <span
                            className={`ml-2 font-bold ${
                              k.trendDirection === 'up'
                                ? 'text-emerald-500'
                                : k.trendDirection === 'down'
                                  ? 'text-red-500'
                                  : 'text-gray-400 dark:text-gray-500'
                            }`}
                          >
                            {k.trendDirection === 'up' ? '↑' : k.trendDirection === 'down' ? '↓' : '→'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <div className={`${SUBCARD} p-4`}>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 text-center">Brand Mentions in AI Answers</h4>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className={inner}>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{ai.llmMentions?.totalMentions || 0}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
                </div>
                <div className={inner}>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{ai.llmMentions?.platforms?.google || 0}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Google AI</p>
                </div>
                <div className={inner}>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{ai.llmMentions?.platforms?.chat_gpt || 0}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">ChatGPT</p>
                </div>
              </div>
              {(ai.llmMentions?.sampleQuestions || []).length > 0 ? (
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">AI questions where the business appears:</p>
                  {ai.llmMentions.sampleQuestions.map((q, i) => (
                    <p key={i} className="text-sm italic text-gray-600 dark:text-gray-300">“{q.question}”</p>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                  Not cited in AI answers yet. A clear opportunity to capture AI-driven traffic.
                </p>
              )}
            </div>

            <div className={`${SUBCARD} p-4`}>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 text-center">Top Domains AI Tools Cite</h4>
              {(ai.competitors || []).length > 0 ? (
                <div className="space-y-2">
                  {ai.competitors.map((c, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700">
                      <span className="text-sm truncate text-gray-700 dark:text-gray-200">{i + 1}. {c.domain}</span>
                      <span className="text-sm font-medium ml-2 text-gray-500 dark:text-gray-400">{(c.mentions || 0).toLocaleString()} mentions</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center">No competitor citation data available for these keywords.</p>
              )}
            </div>
          </div>

          <p className="text-center text-sm mt-4 text-gray-700 dark:text-gray-300">
            💡 {ai.keyInsight || 'AI search is a fast-growing way customers discover local businesses.'}
          </p>
        </>
      )}
    </div>
  );
};

/* ---------- Plain-English summary tab ---------- */

const LEVELS = [
  { tone: 'red', label: 'Needs attention' },
  { tone: 'amber', label: 'Getting there' },
  { tone: 'green', label: 'Looking good' },
];

// 0 = poor, 1 = ok, 2 = good. `invert` for metrics where lower is better.
const levelFor = (value, lo, hi, invert = false) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  if (invert) return n <= lo ? 2 : n <= hi ? 1 : 0;
  return n < lo ? 0 : n < hi ? 1 : 2;
};

const fmt = (n) => (n === null || n === undefined ? '—' : Math.round(Number(n)).toLocaleString());

const LevelMeter = ({ level }) => (
  <div className="flex gap-1.5 w-full">
    {[0, 1, 2].map((i) => (
      <div
        key={i}
        className={`h-2 flex-1 rounded-full ${
          level !== null && i <= level ? TONES[LEVELS[level].tone].bar : 'bg-gray-200 dark:bg-gray-700'
        }`}
      />
    ))}
  </div>
);

const SummaryCard = ({ emoji, question, headline, level, text }) => {
  const meta = level === null ? null : LEVELS[level];
  return (
    <div className={`${SUBCARD} p-5 flex flex-col`}>
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center text-xl flex-shrink-0">
          {emoji}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{question}</p>
          <p className="mt-0.5 text-lg font-bold tracking-tight text-gray-900 dark:text-white">{headline}</p>
        </div>
        {meta && (
          <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${TONES[meta.tone].pill}`}>
            {meta.label}
          </span>
        )}
      </div>
      <div className="mt-4">
        <LevelMeter level={level} />
      </div>
      <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-300">{text}</p>
    </div>
  );
};

const buildSummaryCards = (data, hasOnPage) => {
  const seo = hasOnPage ? data.seoScore : null;
  const visits = data.organicTraffic?.amount ?? null;
  const keywords = data.organicTraffic?.rankingKeywords ?? data.rankedKeywords?.total ?? null;
  const top10 = data.rankedKeywords?.top10 ?? null;
  const speed = hasOnPage ? data.pageAudit?.loadSpeed ?? null : null;
  const backlinks = data.backlinkProfile?.backlinks ?? null;
  const spam = data.backlinkProfile?.spamScore ?? null;
  const critical = hasOnPage ? data.errorsWarnings?.criticalErrors ?? null : null;
  const warnings = hasOnPage ? data.errorsWarnings?.warnings ?? null : null;
  const ai = data.aiSearch || null;
  const aiVolume = ai ? Number(ai.totalAiVolume || 0) : null;
  const aiMentions = ai ? Number(ai.llmMentions?.totalMentions || 0) : null;
  const aiCited = Boolean(ai?.llmMentions?.cited) || (aiMentions !== null && aiMentions > 0);
  const aiTopKeyword = ai?.topKeyword?.keyword || null;
  const aiCompetitors = (ai?.competitors || []).map((c) => c.domain).filter(Boolean).slice(0, 3);

  const healthLevel = levelFor(seo, 50, 75);
  const findLevel = top10 !== null ? levelFor(top10, 3, 15) : levelFor(keywords, 25, 150);
  const speedLevel = levelFor(speed, 2, 4, true);
  const trustLevel = levelFor(backlinks, 20, 200);
  const problemLevel = critical === null ? null : Number(critical) === 0 ? 2 : Number(critical) <= 5 ? 1 : 0;
  const aiLevel = ai === null
    ? null
    : aiCited
      ? 2
      : aiVolume > 0
        ? 0
        : null;
  const aiHeadline = ai === null
    ? 'Not measured yet'
    : aiCited
      ? `Cited in ${fmt(aiMentions)} AI ${aiMentions === 1 ? 'answer' : 'answers'}`
      : aiVolume > 0
        ? 'Not showing up in AI answers'
        : 'Limited AI data so far';
  const aiText = ai === null
    ? 'AI search visibility is checked during the full audit.'
    : aiCited
      ? `AI assistants such as Google's AI Overview and ChatGPT already mention this business when people ask about ${aiTopKeyword ? `"${aiTopKeyword}"` : 'these services'}.${aiVolume > 0 ? ` Tracked search terms get about ${fmt(aiVolume)} AI-driven searches a month, so this is a real source of leads worth protecting.` : ' Being cited at all puts it ahead of most contractors.'}`
      : aiVolume > 0
        ? `People ask AI assistants about these services roughly ${fmt(aiVolume)} times a month, but the business is not being recommended yet.${aiCompetitors.length ? ` AI tools are pointing to ${aiCompetitors.join(', ')} instead.` : ''} Clear service pages, consistent reviews, and structured business details are how to get in.`
        : 'We could not find AI search activity for these keywords yet. AI search is growing fast, so this is worth re-checking after the next audit.';

  return [
    {
      key: 'health',
      emoji: '🩺',
      question: 'Overall site health',
      level: healthLevel,
      headline: seo !== null ? `${Math.round(seo)} out of 100` : 'Not measured yet',
      text:
        seo === null
          ? 'The overall score appears once the page-by-page crawl finishes.'
          : healthLevel === 0
            ? 'There are real issues holding the site back. The good news is that most of them are fixable with focused work.'
            : healthLevel === 1
              ? 'A solid foundation with clear room to improve. A few targeted fixes would move this score quickly.'
              : 'The site is in strong technical shape. Keep it maintained and focus on content and visibility.',
    },
    {
      key: 'found',
      emoji: '🔍',
      question: 'Can people find you on Google?',
      level: findLevel,
      headline:
        visits !== null ? `About ${fmt(visits)} visits a month` : keywords !== null ? `${fmt(keywords)} search terms` : 'No search data yet',
      text:
        keywords === null
          ? 'We could not find search-ranking data for this site yet.'
          : `The site shows up for ${fmt(keywords)} different search terms${
              top10 !== null ? `, and ${fmt(top10)} of those land on page one, which is where almost all the clicks happen` : ''
            }. ${
              findLevel === 2
                ? 'That is a healthy amount of visibility for a contractor.'
                : findLevel === 1
                  ? 'There is visibility to build on, but most searches are not reaching page one yet.'
                  : 'Right now very few searches lead people to this site.'
            }`,
    },
    {
      key: 'speed',
      emoji: '⚡',
      question: 'How fast does it load?',
      level: speedLevel,
      headline: speed !== null ? `About ${speed} seconds` : 'Not measured yet',
      text:
        speed === null
          ? 'Load speed is measured during the page crawl.'
          : speedLevel === 2
            ? 'Pages load quickly. Visitors are not left waiting, and Google rewards fast sites.'
            : speedLevel === 1
              ? 'Pages load at an average pace. Visitors start leaving after about three seconds, so trimming this would help.'
              : 'Pages are slow to load. Many visitors leave before the page even appears, which costs leads and hurts rankings.',
    },
    {
      key: 'trust',
      emoji: '🤝',
      question: 'Do other sites vouch for you?',
      level: trustLevel,
      headline: backlinks !== null ? `${fmt(backlinks)} links from other sites` : 'No link data yet',
      text:
        backlinks === null
          ? 'Backlink data was not available for this site.'
          : `Google treats every link from another website as a vote of confidence. ${
              trustLevel === 0
                ? 'This site has very few, so it is easy for competitors to outrank it.'
                : trustLevel === 1
                  ? 'This is a reasonable start. Directory listings, suppliers, and local press are easy places to earn more.'
                  : 'This is a strong base of trust that competitors will struggle to match.'
            }${spam !== null && Number(spam) > 30 ? ' Some of these links look low quality, which is worth cleaning up.' : ''}`,
    },
    {
      key: 'ai',
      emoji: '🤖',
      question: 'Are AI tools recommending you?',
      level: aiLevel,
      headline: aiHeadline,
      text: aiText,
    },
    {
      key: 'problems',
      emoji: '🛠️',
      question: 'Is anything broken?',
      level: problemLevel,
      headline: critical !== null ? `${fmt(critical)} critical ${Number(critical) === 1 ? 'problem' : 'problems'}` : 'Not checked yet',
      text:
        critical === null
          ? 'Problems are counted during the page crawl.'
          : `${
              Number(critical) === 0
                ? 'We did not find any critical problems.'
                : `We found ${fmt(critical)} critical ${Number(critical) === 1 ? 'problem' : 'problems'}: things like broken links, missing page titles, or pages Google cannot read.`
            }${
              warnings ? ` There ${Number(warnings) === 1 ? 'is' : 'are'} also ${fmt(warnings)} smaller ${Number(warnings) === 1 ? 'warning' : 'warnings'}.` : ''
            } ${Number(critical) === 0 ? 'That is a clean bill of health.' : 'These are usually quick fixes and give an immediate boost.'}`,
    },
  ];
};

const AuditSummaryTab = ({ data, hasOnPage, hasAnyData }) => {
  const cards = buildSummaryCards(hasAnyData ? data : EMPTY_AUDIT_METRICS, hasAnyData && hasOnPage);

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {cards.map((c) => (
          <SummaryCard key={c.key} emoji={c.emoji} question={c.question} headline={c.headline} level={c.level} text={c.text} />
        ))}
      </div>
      {hasAnyData && !hasOnPage && (
        <p className="mt-4 text-xs text-center text-gray-500 dark:text-gray-400">
          Speed, problems, and overall health fill in once the full page crawl finishes.
        </p>
      )}
    </div>
  );
};

const WebsiteAuditOverviewSection = ({ accountId, defaultUrl = '', darkMode = false, readOnly = false }) => {
  const { currentUser } = useAuth();
  const [auditData, setAuditData] = useState(null);
  const [auditedUrl, setAuditedUrl] = useState('');
  const [lastScanned, setLastScanned] = useState('');
  const [loading, setLoading] = useState(true);
  const [auditInProgress, setAuditInProgress] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [auditUrl, setAuditUrl] = useState(defaultUrl);
  const [canRunAudit, setCanRunAudit] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [pendingAudit, setPendingAudit] = useState(null);
  const [clientPipelineLog, setClientPipelineLog] = useState([]);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    setAuditUrl(defaultUrl || '');
  }, [defaultUrl]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setCanRunAudit(false);
      return;
    }
    isInternalUser(currentUser.uid).then(setCanRunAudit).catch(() => setCanRunAudit(false));
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!accountId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const auditsQuery = query(
      collection(db, 'accounts', accountId, 'audits'),
      where('status', '==', 'completed')
    );

    const unsubscribe = onSnapshot(
      auditsQuery,
      (snapshot) => {
        if (snapshot.empty) {
          setAuditData(null);
          setAuditedUrl('');
          setLastScanned('');
          setLoading(false);
          return;
        }

        const sorted = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const aTime = a.createdAt?.toMillis?.() || a.createdAt || 0;
            const bTime = b.createdAt?.toMillis?.() || b.createdAt || 0;
            return bTime - aTime;
          });

        const raw = sorted[0];
        const transformed = transformAuditResults(auditDocToTransformSource(raw));

        setAuditedUrl(raw.url || raw.originalUrl || '');
        setLastScanned(raw.lastScanned || '');
        setAuditData(transformed);
        setAuditInProgress(false);
        setActiveTaskId(null);
        setPendingAudit(null);
        setClientPipelineLog([]);
        setLoading(false);
      },
      (error) => {
        console.error('Error listening to account audits:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return undefined;

    const pendingQuery = query(
      collection(db, 'websiteAudits'),
      where('accountId', '==', accountId),
      where('status', '==', 'pending')
    );

    const unsubscribe = onSnapshot(
      pendingQuery,
      (snapshot) => {
        if (snapshot.empty) {
          if (!submitting) {
            setPendingAudit(null);
            if (!activeTaskId) setAuditInProgress(false);
          }
          return;
        }

        const sorted = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const aTime = a.createdAt?.toMillis?.() || a.createdAt || 0;
            const bTime = b.createdAt?.toMillis?.() || b.createdAt || 0;
            return bTime - aTime;
          });
        const data = sorted[0];
        setPendingAudit(data);
        setActiveTaskId(data.id);
        setAuditInProgress(true);
        if (data.url || data.originalUrl) {
          setAuditUrl((prev) => prev || data.originalUrl || data.url);
        }
      },
      (error) => {
        console.error('Error listening to pending website audits:', error);
      }
    );

    return () => unsubscribe();
  }, [accountId, submitting, activeTaskId]);

  useEffect(() => {
    if (!activeTaskId) return undefined;

    const applyCompleted = (data) => {
      const source = auditDocToTransformSource(data.results ? { ...data.results, url: data.url } : data);
      if (!source) return;
      const transformed = transformAuditResults(source);
      setAuditData(transformed);
      setAuditedUrl(data.url || data.originalUrl || '');
      setLastScanned(source.lastScanned || transformed.lastScanned || '');
      setAuditInProgress(false);
      setActiveTaskId(null);
      setPendingAudit(null);
    };

    const unsubscribe = onSnapshot(doc(db, 'websiteAudits', activeTaskId), (docSnap) => {
      if (!docSnap.exists()) return;
      const data = { id: docSnap.id, ...docSnap.data() };
      setPendingAudit(data);
      if (data.status === 'pending') {
        setAuditInProgress(true);
      } else if (data.status === 'completed') {
        applyCompleted(data);
      } else if (data.status === 'failed') {
        setAuditInProgress(false);
        setSubmitError(data.error || 'Audit failed');
      }
    });

    return () => unsubscribe();
  }, [activeTaskId]);

  // Poll getAuditStatus so on-page crawl can finish without waiting only for the 2-min scheduler
  useEffect(() => {
    if (!activeTaskId || !currentUser?.uid || !auditInProgress) return undefined;

    let cancelled = false;

    const poll = async () => {
      try {
        const result = await getAuditStatus(activeTaskId, currentUser.uid);
        if (cancelled || !result?.success) return;

        if (result.status === 'Ok.' && result.results) {
          const transformed = transformAuditResults(
            auditDocToTransformSource(result.results)
          );
          setAuditData(transformed);
          setAuditInProgress(false);
          if (result.results.lastScanned) {
            setLastScanned(result.results.lastScanned);
          }
        }
      } catch (err) {
        console.warn('Website audit status poll:', err.message);
      }
    };

    poll();
    const interval = setInterval(poll, 10000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [activeTaskId, currentUser?.uid, auditInProgress]);

  const handleRunAudit = async () => {
    if (!auditUrl.trim() || !accountId) return;

    setSubmitting(true);
    setSubmitError(null);
    setAuditInProgress(true);
    setClientPipelineLog([
      {
        step: 'client_call',
        message: 'Calling createWebsiteAudit callable',
        at: new Date().toISOString(),
        fn: 'websiteAuditService.createAccountWebsiteAudit',
      },
    ]);

    try {
      const result = await createAccountWebsiteAudit(auditUrl.trim(), accountId);
      setClientPipelineLog((prev) => [
        ...prev,
        {
          step: 'client_response',
          message: result.taskId
            ? `Task created: ${result.taskId}`
            : 'Callable returned success',
          at: new Date().toISOString(),
          fn: 'createWebsiteAudit',
        },
      ]);
      if (result.taskId) {
        setActiveTaskId(result.taskId);
      }
      setShowModal(false);
    } catch (error) {
      setSubmitError(error.message || 'Failed to start audit');
      setAuditInProgress(false);
      setClientPipelineLog((prev) => [
        ...prev,
        {
          step: 'failed',
          message: error.message || 'Failed to start audit',
          at: new Date().toISOString(),
          fn: 'client',
        },
      ]);
    } finally {
      setSubmitting(false);
    }
  };

  const domainMetricsPartial = useMemo(
    () => metricsFromDomainMetrics(pendingAudit?.domainMetrics),
    [pendingAudit?.domainMetrics]
  );

  const displayMetrics = useMemo(() => {
    const fromCompleted = pendingAudit?.results
      ? transformAuditResults(auditDocToTransformSource(pendingAudit.results))
      : null;
    const completed = auditData || fromCompleted;
    if (completed && domainMetricsPartial) {
      return mergeAuditMetrics(completed, domainMetricsPartial);
    }
    if (completed) return completed;
    if (domainMetricsPartial) return { ...EMPTY_AUDIT_METRICS, ...domainMetricsPartial };
    return EMPTY_AUDIT_METRICS;
  }, [auditData, pendingAudit?.results, domainMetricsPartial]);

  const pipelineStep = pendingAudit?.pipelineStep
    || (submitting ? 'auth_verified' : null);
  const pipelineMessage = pendingAudit?.pipelineMessage
    || pendingAudit?.lastFetchError
    || (submitting ? 'Calling createWebsiteAudit…' : null);
  const pipelineLog = [
    ...clientPipelineLog,
    ...(pendingAudit?.pipelineLog || []),
  ];
  const auditStatus = pendingAudit?.status || (submitting ? 'pending' : null);
  const showPipeline = auditInProgress || submitting || pendingAudit;
  const hasOnPage = hasOnPageMetrics(displayMetrics);
  const hasCompletedAudit = Boolean(auditData) || pendingAudit?.status === 'completed';
  const awaitingOnPage = (auditInProgress || pendingAudit?.status === 'pending') && !hasOnPage;

  const hasAnyData = hasCompletedAudit || hasOnPage || Boolean(domainMetricsPartial);

  const subtitle = loading
    ? 'Loading saved audit…'
    : lastScanned
      ? `Last scanned ${lastScanned}${auditedUrl ? ` · ${auditedUrl}` : ''}`
      : !auditInProgress
        ? 'No completed audit yet. Results fill in when an audit runs.'
        : undefined;

  return (
    <div>
      <SectionHeader
        emoji="🌐"
        title="Website Audit"
        subtitle={subtitle}
        left={
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-600">
            {[
              ['summary', 'Summary'],
              ['technical', 'Technical'],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  activeTab === key
                    ? 'bg-red-600 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        }
        right={
          canRunAudit && !readOnly ? (
            <button
              type="button"
              onClick={() => {
                setSubmitError(null);
                setShowModal(true);
              }}
              disabled={auditInProgress && !submitError}
              className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              Run Audit
            </button>
          ) : null
        }
      />

      {awaitingOnPage && (
        <p className="text-sm text-amber-600 dark:text-amber-400 mb-4 text-center">
          Domain metrics loaded. SEO score, on-page, load speed, and issues appear after the crawl finishes.
        </p>
      )}

      {submitError && (
        <p className="text-red-500 text-sm mb-3 text-center">{submitError}</p>
      )}

      {showPipeline && (
        <AuditPipelineProgress
          pipelineStep={pipelineStep}
          pipelineMessage={pipelineMessage}
          pipelineLog={pipelineLog}
          auditStatus={auditStatus}
          darkMode={darkMode}
        />
      )}

      {activeTab === 'summary' ? (
        <AuditSummaryTab data={displayMetrics} hasOnPage={hasOnPage} hasAnyData={hasAnyData} />
      ) : (
        <>
          <AuditMetricsGrid
            data={displayMetrics}
            hasCompletedAudit={hasOnPage || hasCompletedAudit}
            darkMode={darkMode}
          />
          <AuditExtraDetails data={displayMetrics} hasOnPage={hasOnPage} />
          <AiSearchDetails ai={displayMetrics.aiSearch || null} />
        </>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-950/60 backdrop-blur-sm" onClick={() => setShowModal(false)}>
          <div
            className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col items-center text-center mb-5">
              <span className="w-12 h-12 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center text-2xl mb-3">🌐</span>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Run Website Audit</h3>
            </div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Website URL</label>
            <input
              type="url"
              value={auditUrl}
              onChange={(e) => setAuditUrl(e.target.value)}
              placeholder="https://example.com"
              className={`${INPUT} mb-4`}
            />
            {submitError && (
              <p className="text-red-500 text-sm mb-3">{submitError}</p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRunAudit}
                disabled={submitting || !auditUrl.trim()}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 transition-colors"
              >
                {submitting ? 'Starting...' : 'Start Audit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteAuditOverviewSection;
